import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { ICS2MessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { TemplatesTab } from '../types';

type FormValuesByCountry = Record<string, Record<string, string[]>>;

// The country, declaration type and form type the create drawer offers, as lists keyed one under the other.
// EU (ICS2) has no product templates, so it is only offered on the declaration tab.
export const getTemplateFormValues = (mode: TemplatesTab): FormValuesByCountry => {
    const isProduct = mode === 'product';

    return {
        IRELAND: {
            IMPORT: isProduct ? ['H1', 'H1 Bulk Upload', 'H7', 'I1'] : ['H1', 'H7', 'I1'],
            EXPORT: ['B1', 'B2', 'B3', 'B4', 'C1', 'C2'],
            NCTS: ['D1', 'D2', 'D3', 'D4'],
        },
        UK: {
            IMPORT: isProduct ? ['H1', 'H1 Bulk Upload', 'H2'] : ['H1', 'H2'],
            EXPORT: ['B1'],
            NCTS: ['D1', 'D2', 'D3', 'D4'],
        },
        ...(mode === 'declaration' ? { EU: { ENS: [ICS2MessageTypes.F11, ICS2MessageTypes.F50] as string[] } } : {}),
    };
};

interface Props {
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    formType: MessageTypes;
    mode: TemplatesTab;
}

export interface CreateTemplateInitialValues {
    country?: string;
    declarationType?: string;
    formType?: string;
}

// Pre-fills the create drawer from the filters applied on the dashboard. A field is filled only when the
// drawer offers that value for the fields above it, so the drawer never opens with an impossible combination.
export const getCreateTemplateInitialValues = ({
    country,
    internalType,
    formType,
    mode,
}: Props): CreateTemplateInitialValues => {
    const formValues = getTemplateFormValues(mode);

    const countryKey = country?.toUpperCase();
    const declarationTypes = countryKey ? formValues[countryKey] : undefined;
    if (!countryKey || !declarationTypes) return {};

    const declarationTypeKey = internalType?.toUpperCase();
    const formTypes = declarationTypeKey ? declarationTypes[declarationTypeKey] : undefined;
    if (!declarationTypeKey || !formTypes) return { country: countryKey };

    const formTypeKey = formType?.toUpperCase();
    // Compared upper-case on both sides, because 'H1 Bulk Upload' is stored mixed-case.
    const offeredFormType = formTypes.find((offered) => offered.toUpperCase() === formTypeKey);
    if (!offeredFormType) return { country: countryKey, declarationType: declarationTypeKey };

    return { country: countryKey, declarationType: declarationTypeKey, formType: offeredFormType };
};
